// Utility function to update app metadata (favicon, title, etc.) based on club info
export const updateAppMetadata = (clubInfo) => {
  if (!clubInfo) return;

  // Update page title
  document.title = clubInfo.name ? `${clubInfo.name} - Club Management` : 'ClubHudl';

  // Update various favicon and app icons if club has a logo
  if (clubInfo.logo) {
    // Standard favicon
    updateOrCreateLink('icon', clubInfo.logo, 'image/png');
    
    // Apple touch icons
    updateOrCreateLink('apple-touch-icon', clubInfo.logo);
    updateOrCreateAppleTouchIcon(clubInfo.logo, '180x180');
    updateOrCreateAppleTouchIcon(clubInfo.logo, '167x167');
    updateOrCreateAppleTouchIcon(clubInfo.logo, '152x152');

    // Update Open Graph image
    updateOrCreateMeta('og:image', clubInfo.logo);
    
    // Update Twitter image
    updateOrCreateMeta('twitter:image', clubInfo.logo);
  }

  // Update app name in meta tags
  if (clubInfo.name) {
    updateOrCreateMeta('og:title', `${clubInfo.name} - Club Management`);
    updateOrCreateMeta('twitter:title', `${clubInfo.name} - Club Management`);
    updateOrCreateMeta('apple-mobile-web-app-title', clubInfo.name);
    
    // Update manifest dynamically if possible
    updateManifest(clubInfo);
  }

  // Update description if available
  if (clubInfo.description) {
    updateOrCreateMeta('description', clubInfo.description);
    updateOrCreateMeta('og:description', clubInfo.description);
    updateOrCreateMeta('twitter:description', clubInfo.description);
  }
};

// Helper function to update or create a link element
function updateOrCreateLink(rel, href, type = null) {
  let link = document.querySelector(`link[rel="${rel}"]`);
  
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  
  link.href = href;
  if (type) {
    link.type = type;
  }
}

// Helper function specifically for Apple touch icons with sizes
function updateOrCreateAppleTouchIcon(href, sizes = null) {
  let selector = sizes 
    ? `link[rel="apple-touch-icon"][sizes="${sizes}"]`
    : 'link[rel="apple-touch-icon"]:not([sizes])';
    
  let link = document.querySelector(selector);
  
  if (!link) {
    link = document.createElement('link');
    link.rel = 'apple-touch-icon';
    if (sizes) link.sizes = sizes;
    document.head.appendChild(link);
  }
  
  link.href = href;
}

// Helper function to update or create meta tags
function updateOrCreateMeta(property, content) {
  // Try to find by property first (for Open Graph tags)
  let meta = document.querySelector(`meta[property="${property}"]`);
  
  // If not found, try by name
  if (!meta) {
    meta = document.querySelector(`meta[name="${property}"]`);
  }
  
  if (!meta) {
    meta = document.createElement('meta');
    
    // Determine if it's an Open Graph tag or regular meta tag
    if (property.startsWith('og:') || property.startsWith('twitter:')) {
      meta.setAttribute('property', property);
    } else {
      meta.setAttribute('name', property);
    }
    
    document.head.appendChild(meta);
  }
  
  meta.content = content;
}

// Function to dynamically update manifest
function updateManifest(clubInfo) {
  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (!manifestLink) return;

  // Create a new manifest object
  const manifest = {
    name: clubInfo.name || 'ClubHudl',
    short_name: clubInfo.shortName || clubInfo.name?.substring(0, 12) || 'ClubHudl',
    description: clubInfo.description || 'Club Management Made Easy',
    start_url: '/',
    display: 'standalone',
    theme_color: '#16a34a',
    background_color: '#ffffff',
    icons: []
  };

  // If club has a logo, use it for all icon sizes
  if (clubInfo.logo) {
    const iconSizes = ['72', '96', '128', '144', '152', '192', '384', '512'];
    manifest.icons = iconSizes.map(size => ({
      src: clubInfo.logo,
      sizes: `${size}x${size}`,
      type: 'image/png',
      purpose: 'any maskable'
    }));
  }

  // Convert manifest to data URL and update the link
  const manifestString = JSON.stringify(manifest);
  const manifestBlob = new Blob([manifestString], { type: 'application/json' });
  const manifestURL = URL.createObjectURL(manifestBlob);
  
  manifestLink.href = manifestURL;
}

// Function to update theme color based on club preferences
export const updateThemeColor = (color) => {
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta && color) {
    themeColorMeta.content = color;
  }
};